import { Component } from '@theme/component';

/**
 * Mobile drawer Level-3 banners.
 *
 * The mega-menu-banner blocks are rendered once at section scope (a hidden
 * `.header-mega-banner-source`), so the drawer can't read them at Liquid render
 * time. This clones each banner card into the matching N2's Level-3 banner slot
 * (`.menu-drawer__l3-banners[data-n2-handle]`), where CSS lays them out as a
 * horizontal swipeable row (Bruno Vassari pattern). Idempotent + observed so it
 * survives the header re-rendering via the Section Rendering API.
 *
 * @extends {Component}
 */
class DrawerMegaBanners extends Component {
  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#adopt();
    this.#observer = new MutationObserver(() => this.#adopt());
    this.#observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #adopt = () => {
    const root = this.closest('#header-component') ?? document;
    const source = root.querySelector('.header-mega-banner-source');
    if (!source) return;

    /** @type {Map<string, Element[]>} */
    const byHandle = new Map();
    for (const item of source.querySelectorAll('.header-mega-banner-source__item')) {
      const handle = /** @type {HTMLElement} */ (item).dataset.n2Handle;
      const card = item.querySelector('.mega-panel__banner');
      if (!handle || !card) continue;
      if (!byHandle.has(handle)) byHandle.set(handle, []);
      byHandle.get(handle)?.push(card);
    }
    if (byHandle.size === 0) return;

    this.#observer?.disconnect();
    for (const slot of this.querySelectorAll('.menu-drawer__l3-banners[data-n2-handle]')) {
      // QA #2: prefer the composite "n1__n2" key, else fall back to the bare N2 handle.
      const el = /** @type {HTMLElement} */ (slot);
      const cards = byHandle.get(el.dataset.n2Handle ?? '') ?? byHandle.get(el.dataset.n2 ?? '');
      if (!cards) continue;
      // Idempotent: only rewrite when not already populated correctly.
      if (slot.childElementCount === cards.length) continue;
      slot.replaceChildren(...cards.map((card) => card.cloneNode(true)));
      // The slot sits inside its N2's closed <details>, so once the URLs are real,
      // loading="lazy" holds the download until that submenu is opened.
      loadBannerImages(slot);
    }
    if (this.isConnected) {
      this.#observer?.observe(this, { childList: true, subtree: true });
    }
  };
}

/**
 * Promote deferred banner image URLs (data-sizes / data-srcset / data-src) to the real
 * attributes. They are deferred because cloning an <img> that already has src/srcset
 * makes Chrome fetch it immediately (see blocks/mega-menu-banner.liquid).
 * @param {ParentNode} root
 */
function loadBannerImages(root) {
  for (const img of root.querySelectorAll('img[data-src]')) {
    const { sizes, srcset, src } = /** @type {HTMLImageElement} */ (img).dataset;
    if (sizes) img.setAttribute('sizes', sizes);
    if (srcset) img.setAttribute('srcset', srcset);
    if (src) img.setAttribute('src', src);
    img.removeAttribute('data-sizes');
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-src');
  }
}

if (!customElements.get('drawer-mega-banners')) {
  customElements.define('drawer-mega-banners', DrawerMegaBanners);
}
